import type { Translations } from '@/i18n/types';

/** Source type discriminator shared by the source forms */
export type SourceCredentialType = 'Dataverse' | 'BusinessCentral' | 'Salesforce';

/**
 * Labels used for the shared client credential fields of a source form.
 * Salesforce Connected Apps name these "Consumer Key" and "Secret", whereas
 * the Microsoft (Azure AD) sources name them "Client ID" and "Client Secret".
 */
export interface SourceCredentialLabels {
  /** Label for the client identifier field */
  clientId: string;
  /** Placeholder for the client identifier field */
  clientIdPlaceholder: string;
  /** Label for the client secret field */
  clientSecret: string;
  /** Placeholder for the client secret field */
  clientSecretPlaceholder: string;
}

/**
 * Resolves the credential field labels/placeholders used by the source creation wizard
 * for the given source type.
 */
export function getWizardCredentialLabels(
  sourceType: SourceCredentialType,
  t: Translations,
): SourceCredentialLabels {
  if (sourceType === 'Salesforce') {
    return {
      clientId: t.wizard.source.consumerKey,
      clientIdPlaceholder: t.wizard.source.consumerKeyPlaceholder,
      clientSecret: t.wizard.source.secret,
      clientSecretPlaceholder: t.wizard.source.secretPlaceholder,
    };
  }

  return {
    clientId: t.wizard.source.clientId,
    clientIdPlaceholder: t.wizard.source.clientIdPlaceholder,
    clientSecret: t.wizard.source.clientSecret,
    clientSecretPlaceholder: t.wizard.source.clientSecretPlaceholder,
  };
}

/** Labels used for the credential fields of the edit source dialog */
export interface EditSourceCredentialLabels extends SourceCredentialLabels {
  /** Helper text shown below the client identifier field */
  clientIdDescription: string;
  /** Helper text shown below the client secret field */
  clientSecretDescription: string;
  /** Validation message shown when the client identifier is missing */
  clientIdRequired: string;
  /** Validation message shown when the client secret is missing */
  clientSecretRequired: string;
}

/**
 * Resolves the credential field labels, placeholders, descriptions and validation
 * messages used by the edit source dialog for the given source type.
 */
export function getEditSourceCredentialLabels(
  sourceType: SourceCredentialType,
  t: Translations,
): EditSourceCredentialLabels {
  if (sourceType === 'Salesforce') {
    return {
      clientId: t.sources.editDialog.consumerKey,
      clientIdPlaceholder: t.wizard.source.consumerKeyPlaceholder,
      clientIdDescription: t.sources.editDialog.consumerKeyDescription,
      clientIdRequired: t.sources.editDialog.consumerKeyRequired,
      clientSecret: t.sources.editDialog.secret,
      clientSecretPlaceholder: t.wizard.source.secretPlaceholder,
      clientSecretDescription: t.sources.editDialog.secretDescription,
      clientSecretRequired: t.sources.editDialog.secretRequired,
    };
  }

  return {
    clientId: t.sources.editDialog.clientId,
    clientIdPlaceholder: t.sources.createDialog.clientIdPlaceholder,
    clientIdDescription: t.sources.editDialog.clientIdDescription,
    clientIdRequired: t.sources.editDialog.clientIdRequired,
    clientSecret: t.sources.editDialog.clientSecret,
    clientSecretPlaceholder: t.sources.createDialog.clientSecretPlaceholder,
    clientSecretDescription: t.sources.editDialog.clientSecretDescription,
    clientSecretRequired: t.sources.editDialog.clientSecretRequired,
  };
}
